/**
 * Consumo di flussi Server-Sent Events.
 *
 * `EventSource` del browser sa fare solo richieste GET, mentre avviare un deploy
 * è una POST: il flusso va quindi letto dal corpo della risposta e interpretato
 * qui. Il parser è separato dal trasporto per poterlo verificare sui casi che
 * contano davvero — un evento spezzato fra due pacchetti di rete.
 */

export interface SseEvent {
  readonly event: string;
  readonly data: string;
}

export interface SseParser {
  push(chunk: string): SseEvent[];
}

export function createSseParser(): SseParser {
  let buffer = "";

  /** Interpreta un blocco già completo (le righe fra due a capo doppi). */
  const parseBlock = (block: string): SseEvent | null => {
    let event = "message";
    const data: string[] = [];

    for (const line of block.split("\n")) {
      // Le righe che iniziano con i due punti sono commenti: servono a tenere
      // viva la connessione attraverso i proxy.
      if (line.startsWith(":")) continue;

      const separator = line.indexOf(":");
      const field = separator === -1 ? line : line.slice(0, separator);
      const value = separator === -1 ? "" : line.slice(separator + 1).replace(/^ /, "");

      if (field === "event") event = value;
      else if (field === "data") data.push(value);
    }

    return data.length === 0 ? null : { event, data: data.join("\n") };
  };

  return {
    push(chunk) {
      buffer += chunk.replaceAll("\r\n", "\n");

      const blocks = buffer.split("\n\n");
      // L'ultimo elemento non è terminato: resta in attesa del prossimo pacchetto.
      buffer = blocks.pop() ?? "";

      return blocks.flatMap((block) => {
        const parsed = parseBlock(block);
        return parsed ? [parsed] : [];
      });
    },
  };
}

/**
 * Esegue una richiesta e ne consuma la risposta come flusso di eventi.
 *
 * Restituisce un iteratore asincrono: l'interfaccia può aggiornare la schermata
 * a ogni riga di log, invece di attendere la fine dell'operazione.
 */
export async function* streamEvents<T>(
  path: string,
  options: { method?: string; signal?: AbortSignal } = {},
): AsyncGenerator<{ event: string; data: T }> {
  const response = await fetch(`/api${path}`, {
    method: options.method ?? "POST",
    credentials: "same-origin",
    headers: { accept: "text/event-stream" },
    signal: options.signal,
  });

  // Chiave e parametri come per le altre chiamate: chi mostra l'errore lo traduce.
  if (!response.ok) throw await toApiError(response);
  if (!response.body) throw new ApiError(response.status, "HTTP_ERROR", "", undefined, "errori.http", { status: response.status });

  // Il server può rispondere in JSON invece che con un flusso: accade quando
  // non c'è alcuna operazione da seguire. Non è un errore, semplicemente non
  // c'è nulla da mostrare.
  if (!response.headers.get("content-type")?.includes("text/event-stream")) {
    await response.body.cancel();
    return;
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  const parser = createSseParser();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      for (const event of parser.push(value)) {
        if (event.event === "end") return;

        yield { event: event.event, data: JSON.parse(event.data) as T };
      }
    }
  } finally {
    // Chiude la connessione anche se il consumatore interrompe il ciclo,
    // così il server non resta a scrivere su un flusso che nessuno legge.
    reader.cancel().catch(() => {});
  }
}import { ApiError, toApiError } from "./api.ts";

